import React, { useEffect, useState } from 'react'
import { Alert, Card, Typography, Spin, Badge, Tag, Space, Divider } from 'antd'
import { useSelector } from 'react-redux'
import { useSwipeable } from 'react-swipeable'
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import PrevNextFloatingButtons from '../../../../../Reusable Components/Buttons/PrevNextFloatingButtons'
import '../../../styling/ResultDetail.css'
import StatsCard from './components/StatsCard'

const { Title, Text } = Typography

const ResultDetail = ({ result, onBack, onNext, onPrevious, hasNext, hasPrevious }) => {
  const careSyncDataPoints = useSelector((state) => state.careSyncDataPoints.data)
  const [careSyncResult, setCareSyncResult] = useState(result)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!result.dataPoint) {
      handleFetchInfo()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFetchInfo = async () => {
    setLoading(true)
    try {
      let tmpDataPoint = careSyncDataPoints?.find((dp) => dp.key === result.key)

      if (tmpDataPoint) {
        setCareSyncResult({ ...careSyncResult, dataPoint: tmpDataPoint })
      }
    } catch (error) {
      console.error('Error fetching data point:', error)
    } finally {
      setLoading(false)
    }
  }

  // Find the corresponding dataPoint template
  const dataPoint = careSyncDataPoints?.find((dp) => dp.key === result.key)

  // Set up swipe handlers for mobile navigation
  const handlers = useSwipeable({
    onSwipedLeft: () => hasNext && onNext(),
    onSwipedRight: () => hasPrevious && onPrevious(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  })

  // Parse reference range data
  const referenceRanges = dataPoint?.data ? JSON.parse(dataPoint.data).referenceRangeData : []

  // Create metrics object for StatsCard
  const metrics = {
    current: {
      value: result.value,
      unit: dataPoint?.suffix
    },
    referenceRanges: referenceRanges
  }

  // Get result status
  const getResultStatus = () => {
    if (!result.value || !referenceRanges || referenceRanges.length === 0) {
      return 'unknown'
    }

    const value = parseFloat(result.value)
    let isNormal = false

    for (const range of referenceRanges) {
      const min = range.min !== undefined ? parseFloat(range.min) : null
      const max = range.max !== undefined ? parseFloat(range.max) : null

      if ((min === null || value >= min) && (max === null || value <= max)) {
        isNormal = true
        break
      }
    }

    return isNormal ? 'normal' : 'abnormal'
  }

  const resultStatus = getResultStatus()

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Handle back button click
  const handleBackClick = (e) => {
    e.stopPropagation()
    if (onBack) {
      onBack()
    }
  }

  return (
    <div className='result-detail-container' {...handlers}>
      <div className='result-detail-header'>
        <button className='back-button' onClick={handleBackClick}>
          <ArrowLeftOutlined />
        </button>
        <div className='test-info'>
          <Title level={4}>{dataPoint?.label || 'Test Result'}</Title>
          <Space size={8}>
            <span className='date-badge'>
              <CalendarOutlined />
              <Text>{formatDate(result.dateCreated)}</Text>
            </span>
            {resultStatus === 'normal' ? (
              <Tag color='success' icon={<CheckCircleOutlined />}>
                Normal
              </Tag>
            ) : resultStatus === 'abnormal' ? (
              <Tag color='error' icon={<ExclamationCircleOutlined />}>
                Abnormal
              </Tag>
            ) : (
              <Tag color='default' icon={<InfoCircleOutlined />}>
                Unspecified
              </Tag>
            )}
          </Space>
        </div>
      </div>

      {loading ? (
        <div className='loading-container'>
          <Spin size='large' tip='Loading result details...' />
        </div>
      ) : (
        <div className='result-detail-content'>
          <Card className='result-card main-value'>
            <StatsCard metrics={metrics} result={result} dataPoint={dataPoint} />
          </Card>

          <Card className='result-card reference-ranges'>
            <Title level={5}>Reference Ranges</Title>
            <Divider className='slim-divider' />

            {careSyncResult.dataPoint?.data && JSON.parse(careSyncResult.dataPoint?.data)?.referenceRangeData ? (
              <Space direction='vertical' className='range-list'>
                {JSON.parse(careSyncResult.dataPoint.data).referenceRangeData.map((range, index) => (
                  <Alert key={index} type='info' showIcon message={range.label} className='range-alert' />
                ))}
              </Space>
            ) : (
              <div className='no-ranges'>
                <Text type='secondary'>No reference ranges available</Text>
              </div>
            )}
          </Card>

          {/* Navigation guidance */}
          <div className='swipe-guidance'>
            <Badge status='processing' text={<Text type='secondary'>Swipe to navigate between results</Text>} />
          </div>
        </div>
      )}

      {/* Override PrevNextFloatingButtons to properly call onBack */}
      <PrevNextFloatingButtons onPrev={onBack} prevOnly prevText='Back to Results' />
    </div>
  )
}

export default ResultDetail
